import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import {
  AddressFields,
  addressToRequest,
  emptyAddress,
  type AddressValue,
} from "@/components/AddressFields";
import {
  BankDetailsFields,
  bankDetailsToRequest,
  emptyBankDetails,
  type BankDetailsValue,
} from "@/components/BankDetailsFields";
import type { components } from "@dolfin/api-admin";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Spinner } from "@/components/ui/spinner";

type SupplierResponse = components["schemas"]["SupplierResponse"];

/** Optional prefill, e.g. a bill's OCR-extracted supplier details. */
export interface CreateSupplierInitialValues {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  taxNumber?: string | null;
  registrationNumber?: string | null;
  /** Free-text address (bills store address as one string); seeds address line 1. */
  addressLine1?: string | null;
}

export interface CreateSupplierDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (supplier: SupplierResponse) => void;
  /** Prefill the form (e.g. from a bill). Applied each time the dialog opens. */
  initialValues?: CreateSupplierInitialValues;
  description?: string;
}

export function CreateSupplierDialog({
  open,
  onOpenChange,
  onCreated,
  initialValues,
  description,
}: CreateSupplierDialogProps) {
  const api = useDolfinClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [taxNumber, setTaxNumber] = useState("");
  const [registrationNumber, setRegistrationNumber] = useState("");
  const [address, setAddress] = useState<AddressValue>(emptyAddress);
  const [bankDetails, setBankDetails] = useState<BankDetailsValue>(emptyBankDetails);
  const [paymentTermsDefault, setPaymentTermsDefault] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed on open only: an inline initialValues object would otherwise clobber the reviewer's edits.
  useEffect(() => {
    if (!open) return;
    setName(initialValues?.name ?? "");
    setEmail(initialValues?.email ?? "");
    setPhone(initialValues?.phone ?? "");
    setWebsite(initialValues?.website ?? "");
    setTaxNumber(initialValues?.taxNumber ?? "");
    setRegistrationNumber(initialValues?.registrationNumber ?? "");
    setAddress(
      initialValues?.addressLine1
        ? { ...emptyAddress, line1: initialValues.addressLine1 }
        : emptyAddress,
    );
    setBankDetails(emptyBankDetails);
    setPaymentTermsDefault("");
    setSubmitting(false);
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError("Name is required.");
      return;
    }

    setSubmitting(true);
    const { data, error: apiError } = await api.POST("/v1/suppliers", {
      body: {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.trim() || null,
        website: website.trim() || null,
        taxNumber: taxNumber.trim() || null,
        registrationNumber: registrationNumber.trim() || null,
        address: addressToRequest(address),
        bankDetails: bankDetailsToRequest(bankDetails),
        paymentTermsDefault: paymentTermsDefault.trim() || null,
      },
    });

    if (apiError || !data) {
      const message = apiErrorMessage(apiError, "Failed to create supplier.");
      setError(message);
      toast.error(message);
      setSubmitting(false);
      return;
    }

    setSubmitting(false);
    handleOpenChange(false);
    toast.success("Supplier created");
    onCreated(data);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Create Supplier</DialogTitle>
            <DialogDescription>
              {description ?? "Add a new supplier to your organisation."}
            </DialogDescription>
          </DialogHeader>

          <div className="mt-4 max-h-[65vh] space-y-4 overflow-y-auto pr-1">
            <div className="space-y-2">
              <Label htmlFor="supplier-name">Name</Label>
              <Input
                id="supplier-name"
                placeholder="e.g. Acme Ltd"
                value={name}
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="supplier-email">Email</Label>
                <Input
                  id="supplier-email"
                  type="email"
                  placeholder="billing@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier-phone">Phone</Label>
                <Input
                  id="supplier-phone"
                  placeholder="e.g. +44 20 1234 5678"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="supplier-website">Website</Label>
                <Input
                  id="supplier-website"
                  placeholder="e.g. acme.com"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier-payment-terms">Payment Terms</Label>
                <Input
                  id="supplier-payment-terms"
                  placeholder="e.g. Net 30"
                  value={paymentTermsDefault}
                  onChange={(e) => setPaymentTermsDefault(e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="supplier-tax">Tax Number</Label>
                <Input
                  id="supplier-tax"
                  placeholder="e.g. GB123456789"
                  value={taxNumber}
                  onChange={(e) => setTaxNumber(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="supplier-registration">Registration Number</Label>
                <Input
                  id="supplier-registration"
                  placeholder="e.g. 01234567"
                  value={registrationNumber}
                  onChange={(e) => setRegistrationNumber(e.target.value)}
                />
              </div>
            </div>

            <AddressFields value={address} onChange={setAddress} />

            <BankDetailsFields value={bankDetails} onChange={setBankDetails} />

            {error && <p className="text-destructive text-sm">{error}</p>}
          </div>

          <DialogFooter className="mt-6">
            <Button type="submit" disabled={submitting}>
              {submitting && <Spinner className="size-4" />}
              Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
